/**
 * Scroll Snap
 *
 * Example HTML:
 *
 * <main class="js-scroll-snap">
 * 	<section></section>
 * 	<section></section>
 * 	<section></section>
 * 	<section></section>
 * </main>
 */

const passiveIsSupported = require('../helpers/featuredetect-passive');
const { getOffset } = require('./navbar');

const init = function() {
	// Container and Sections
	const $container = document.querySelector('.js-scroll-snap');
	if (!$container) return;
	// Previous element of container
	const $hero = $container.previousElementSibling;
	// All direct section children, matches what BE renders. Conveniently won't affect divs and scripts.
	const $sections = $container.querySelectorAll(':scope > section');

	let scrollTimeout, isInteracting, isAutomatedScrolling;
	const delay = 50;
	let isScrollingDown = false;

	/**
	 * Determine which element to snap to,
	 * based on which element matches in view requirements
	 * and snap using scrollIntoView.
	 */
	const findAndScroll = () => {
		// Prevent automated scroll from triggering findAndScroll when scrollTo is completed,
		// since scroll listener is set on the same element that we execute scrollTo on
		if (isAutomatedScrolling) {
			isAutomatedScrolling = false;
			return;
		}
		const $inviews = [];

		const containerRect = $container.getBoundingClientRect();
		// Prevent locking the user in our container by scrolling back to the first/last when they're trying to leave
		const mayScrollUp = containerRect.top < 0;
		const mayScrollDown = containerRect.top + containerRect.height - window.innerHeight >= 0;

		$sections.forEach($box => {
			const rect = $box.getBoundingClientRect();
			const isInView = 
				(rect.top > 0 - rect.height) && // Not disappeared at the top
				(rect.top < rect.height) ; // Not disappeared at the bottom

			if (isInView) {
				if (!isScrollingDown && mayScrollUp) {
					$inviews.push($box);
				} else if (!isScrollingDown && !mayScrollUp && $hero) {
					$inviews.push($hero);
				} else if (isScrollingDown && mayScrollDown) {
					$inviews.push($box);
				}
			}
		});

		if ($inviews.length > 0) {
			let $chosenOne;
			if ($inviews.length === 1) {
				$chosenOne = $inviews[0];
			} else {
				$chosenOne = isScrollingDown ? $inviews[$inviews.length - 1] : $inviews[0];
			}
			// Prevent automated scroll from triggering findAndScroll when scrollTo is completed
			isAutomatedScrolling = true;
			// Scroll window for element to be right underneath navbar
			window.scrollTo({
				top: $chosenOne.getBoundingClientRect().top + window.scrollY - getOffset(),
				behavior: 'smooth'
			});
		}
	};

	/**
	 * Event listeners that prevent or allow findAndScroll.
	 */
	const onInteractStart = () => {
		isInteracting = true;
	};

	const onInteractStop = () => {
		isInteracting = false;
		findAndScroll();
	};

	const onInteract = () => {
		onScroll();
	};

	// The main function that determines whether we can take over scroll.
	// Works by setting a timeout that gets cleared and reset on every call.
	// If it stops getting cleared, it'll actually be executed,
	// which means we can take over scroll.
	let lastScrollTop = 0;
	const onScroll = () => {
		clearTimeout(scrollTimeout);
		const newScrollTop = window.pageYOffset || document.documentElement.scrollTop;

		// Update scroll direction
		if (newScrollTop !== lastScrollTop) {
			isScrollingDown = newScrollTop > lastScrollTop;
			lastScrollTop = newScrollTop;
		}

		if (isInteracting) {
			return;
		}

		scrollTimeout = setTimeout(findAndScroll, delay);
	};

	// Listeners to determine when we're allowed to findAndScroll
	window.addEventListener(
		'keydown',
		onInteractStart,
		passiveIsSupported && { passive: true }
	);
	window.addEventListener(
		'keyup',
		onInteractStop,
		passiveIsSupported && { passive: true }
	);
	window.addEventListener(
		'mousedown',
		onInteractStart,
		passiveIsSupported && { passive: true }
	);
	window.addEventListener(
		'mouseup',
		onInteractStop,
		passiveIsSupported && { passive: true }
	);
	window.addEventListener(
		'touchstart',
		onInteractStart,
		passiveIsSupported && { passive: true }
	);
	window.addEventListener(
		'touchend',
		onInteractStop,
		passiveIsSupported && { passive: true }
	);
	window.addEventListener('scroll', onScroll, passiveIsSupported && { passive: true });
	window.addEventListener('wheel', onInteract, passiveIsSupported && { passive: true });
};

module.exports = {
	init
};
